import { FieldArray } from 'formik';
import produce from 'immer';
import { filter, map, uniqueId } from 'lodash';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { DragDropContext, DraggableProvidedDragHandleProps } from 'react-beautiful-dnd';
import styled from 'styled-components/macro';
import { assertNotNull } from '../../../../helpers/nullHelpers';
import { DraggableContainer } from '../../../../shared/DraggableContainer';
import { DroppableContainer } from '../../../../shared/DroppableContainer';
import { FormContext } from '../../../../shared/form/Form';
import { colourNeutral07, colourNeutral09, colourNeutral11 } from '../../../../styling/colours';
import { spacing03, spacing05, spacing06 } from '../../../../styling/spacing';
import { DesignCalculationsRequest } from '../../../designs/design-form/calculations/designCalculationsResponse';
import { DesignMaterialsCalculationsRequest } from '../../../designs/design-form/calculations/materials/designMaterialsCalculationsResponse';
import { AddItemButton } from '../AddItemButton';
import { dragTypes, handleDragEnd } from '../drag-and-drop/DragEndHandlers';
import { createDuplicateManifold } from '../manifolds/manifoldFormModel';
import { QuoteOrDesignFormContext } from '../QuoteOrDesignFormContext';
import { unitFieldName } from '../quoteOrDesignFormFieldNameHelpers';
import { UnitFields } from './UnitFields';
import { UnitFormModel } from './unitFormModel';

type Props = {
  units: Array<UnitFormModel>;
  designCalculationsRequest?: DesignCalculationsRequest;
  designMaterialsCalculationsRequest?: DesignMaterialsCalculationsRequest;
};

export const UnitsForm = ({ units, designCalculationsRequest, designMaterialsCalculationsRequest }: Props) => {
  const formContext = assertNotNull(useContext(QuoteOrDesignFormContext));

  const { setFieldValue, setFieldTouched } = formContext.formProps;

  const { focusedUnitId } = formContext.formProps.values;
  const isFocusedOnUnit = focusedUnitId != null;

  const [pageStatePreUnitFocus, setPageStatePreUnitFocus] = useState<{
    unitBeingFocusedIsExpanded: boolean;
    scrollPosition: { x: number; y: number };
  }>({ unitBeingFocusedIsExpanded: false, scrollPosition: { x: 0, y: 0 } });

  useEffect(() => {
    if (!isFocusedOnUnit) {
      window.scrollTo(pageStatePreUnitFocus.scrollPosition.x, pageStatePreUnitFocus.scrollPosition.y);
    }
  }, [isFocusedOnUnit]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleUnitFocus = (unitIndex: number) => {
    const unit = units[unitIndex];

    if (!isFocusedOnUnit) {
      setPageStatePreUnitFocus({
        unitBeingFocusedIsExpanded: unit.isExpanded,
        scrollPosition: { x: window.pageXOffset, y: window.pageYOffset },
      });

      setFieldValue(unitFieldName(unitIndex, 'isExpanded'), true);
      setFieldValue('focusedUnitId', unit.id);
    } else {
      setFieldValue(unitFieldName(unitIndex, 'isExpanded'), pageStatePreUnitFocus.unitBeingFocusedIsExpanded);
      setFieldValue('focusedUnitId', null);
    }
  };

  const isInReadOnlyMode = useContext(FormContext).isDisabled;

  const numberOfUnitsCreated = useRef(units.length);

  const createNewUnit = (): UnitFormModel => {
    numberOfUnitsCreated.current += 1;

    return {
      unitId: null,
      id: uniqueId('unit'),
      isExpanded: true,
      totalUnderfloorHeatingAreaInMetresSquared: 0,
      name: `Unit ${numberOfUnitsCreated.current}`,
      quantity: '1',
      numberOfBedrooms: '0',
      numberOfBathrooms: '0',
      cylinderVolumeOption: null,
      hasPipelineMap: formContext.formType == 'Design',
      pipelineMapJson: '',
      waterBufferSizeInLitres: null,
      boilerCapacityInKilowatts: null,
      pumpSelectionType: 'Automatic',
      pumpLocation: 'RemotePump',
      pumps: [null],
      underfloorHeatingExtraParts: [],
      heatPumpExtraParts: [],
      heatSourceTypeCode: 'Boiler',
      heatPump: null,
      solar: null,
      manifolds: [],
      heatLossDetails: null,
      designViewType: null,
    };
  };

  return (
    <Container>
      <DragDropContext onDragEnd={result => handleDragEnd(result, units, setFieldValue, setFieldTouched)}>
        <DroppableContainer droppableId="units" type={dragTypes.unit}>
          {placeholder => {
            return (
              <FieldArray name="units">
                {arrayHelpers => {
                  const addUnit = () => {
                    arrayHelpers.push(createNewUnit());
                  };

                  const duplicateUnitAtIndex = (index: number) => {
                    const newUnit = createDuplicateUnit(units[index]);
                    arrayHelpers.insert(index + 1, newUnit);
                  };

                  return (
                    <>
                      <UnitsContainer>
                        {map(
                          filter(
                            map(units, (unit, unitIndex) => ({ unit, unitIndex })),
                            ({ unit }) => !isFocusedOnUnit || unit.id === focusedUnitId,
                          ),
                          ({ unit, unitIndex }) => {
                            return (
                              <StyledDraggableContainer key={unit.id} draggableId={unit.id} index={unitIndex}>
                                {(unitDragHandleProps: DraggableProvidedDragHandleProps | null) => (
                                  <UnitFields
                                    unit={unit}
                                    unitIndex={unitIndex}
                                    isExpanded={unit.isExpanded}
                                    dragHandleProps={unitDragHandleProps}
                                    deleteUnit={() => arrayHelpers.remove(unitIndex)}
                                    duplicateUnit={() => duplicateUnitAtIndex(unitIndex)}
                                    designCalculationsRequest={designCalculationsRequest}
                                    designMaterialsCalculationsRequest={designMaterialsCalculationsRequest}
                                    onUnitFocus={() => handleUnitFocus(unitIndex)}
                                  />
                                )}
                              </StyledDraggableContainer>
                            );
                          },
                        )}
                        {placeholder}
                      </UnitsContainer>
                      {!isInReadOnlyMode && !isFocusedOnUnit && (
                        <AddUnitButton onClick={addUnit}>+ Add Unit</AddUnitButton>
                      )}
                    </>
                  );
                }}
              </FieldArray>
            );
          }}
        </DroppableContainer>
      </DragDropContext>
    </Container>
  );
};

const Container = styled.div`
  margin-top: ${spacing06};
`;

const UnitsContainer = styled.div`
  background-color: ${colourNeutral07};
`;

const AddUnitButton = styled(AddItemButton)`
  margin-top: ${spacing03};
  margin-bottom: ${spacing05};
`;

const StyledDraggableContainer = styled(DraggableContainer)`
  background: ${colourNeutral11};
  border: solid 3px ${colourNeutral09};
`;

const createDuplicateUnit = (unit: UnitFormModel) => {
  return produce(unit, draftUnit => {
    draftUnit.unitId = null;
    draftUnit.id = uniqueId('unit');
    draftUnit.manifolds = map(draftUnit.manifolds, manifold => createDuplicateManifold(manifold));
  });
};
